import { billing, workspace } from '@lawbee/domain';
import { AuthLayout, Button, Field, FormError, Input, LoadingState, Select } from '@lawbee/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, errorMessage, fieldErrors } from '../../lib/api';
import { PROVINCES, TIMEZONES, useParam, useSearchParam } from '../../lib/hooks';
import { ME_KEY } from '../../lib/me';
import type { Me } from '../../lib/types';
import { Wordmark } from '../../shell/Logo';
import { InvalidLink, isNotFound } from './shared';

const MIN_PASSWORD = 10;

type AuthResponse = { ok: boolean; me: Me };

function LogoLink() {
  return (
    <Link to="/" aria-label="LawBee home">
      <Wordmark className="text-[22px]" markClassName="size-11" />
    </Link>
  );
}

/** Errors shown under fields: server field errors win over local checks once submitted. */
function useFormErrors() {
  const [local, setLocal] = useState<Record<string, string>>({});
  const [server, setServer] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  return {
    errors: { ...local, ...server },
    message,
    setLocal,
    reset: () => {
      setLocal({});
      setServer({});
      setMessage(null);
    },
    fromError: (err: unknown) => {
      const f = fieldErrors(err);
      setServer(f);
      setMessage(Object.keys(f).length > 0 ? 'Please check the highlighted fields.' : errorMessage(err));
    },
  };
}

// ---------------------------------------------------------------------------
// Sign up
// ---------------------------------------------------------------------------

type SignupResponse = { ok: boolean; verificationRequired: boolean; email: string };

/** "Check your email" with a resend button (after sign-up, or when sign-in finds an unverified address). */
function CheckEmail({ email }: { email: string }) {
  const [sent, setSent] = useState(false);
  const resend = useMutation({
    mutationFn: () => api.post('/api/auth/resend-verification', { email }),
    onSuccess: () => setSent(true),
  });
  return (
    <AuthLayout logo={<LogoLink />} title="Check your email" description={<>We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>. Open it to finish setting up LawBee.</>}>
      <div className="space-y-4 text-center">
        <p className="text-sm text-muted-foreground">The link expires in 24 hours. Check your spam folder if it does not arrive.</p>
        <Button variant="outline" className="w-full" loading={resend.isPending} disabled={sent} onClick={() => resend.mutate()}>
          {sent ? 'Sent again' : 'Send the link again'}
        </Button>
        <Link to="/login" className="block text-sm font-medium text-foreground underline-offset-4 hover:underline">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}

export function SignupPage() {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [form, setForm] = useState({ firmName: '', name: '', email: '', password: '', province: 'ON', timezone: 'America/Toronto' });
  const fe = useFormErrors();
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const signup = useMutation({
    mutationFn: () => api.post<SignupResponse>('/api/auth/signup', { ...form, firmName: form.firmName.trim(), name: form.name.trim(), email: form.email.trim() }),
    onSuccess: (res) => setPendingEmail(res.email),
    onError: fe.fromError,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    fe.reset();
    const errs: Record<string, string> = {};
    if (!form.firmName.trim()) errs.firmName = 'Enter your firm name.';
    if (!form.name.trim()) errs.name = 'Enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Enter a valid email address.';
    if (form.password.length < MIN_PASSWORD) errs.password = `Use at least ${MIN_PASSWORD} characters.`;
    if (Object.keys(errs).length > 0) {
      fe.setLocal(errs);
      return;
    }
    signup.mutate();
  };

  if (pendingEmail) return <CheckEmail email={pendingEmail} />;

  return (
    <AuthLayout
      logo={<LogoLink />}
      title="Start your free trial"
      description={`${billing.TRIAL_DAYS} days of every Growth feature. No card needed.`}
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={submit} className="space-y-4">
        <Field label="Firm name" htmlFor="firmName" error={fe.errors.firmName} required>
          <Input id="firmName" autoComplete="organization" value={form.firmName} onChange={set('firmName')} aria-invalid={fe.errors.firmName ? true : undefined} />
        </Field>
        <Field label="Your name" htmlFor="name" error={fe.errors.name} required>
          <Input id="name" autoComplete="name" value={form.name} onChange={set('name')} aria-invalid={fe.errors.name ? true : undefined} />
        </Field>
        <Field label="Work email" htmlFor="email" error={fe.errors.email} required>
          <Input id="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} aria-invalid={fe.errors.email ? true : undefined} />
        </Field>
        <Field label="Password" htmlFor="password" error={fe.errors.password} description={fe.errors.password ? undefined : `At least ${MIN_PASSWORD} characters.`} required>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
            aria-invalid={fe.errors.password ? true : undefined}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Province" htmlFor="province" error={fe.errors.province}>
            <Select id="province" value={form.province} onChange={set('province')} options={PROVINCES} />
          </Field>
          <Field label="Time zone" htmlFor="timezone" error={fe.errors.timezone}>
            <Select id="timezone" value={form.timezone} onChange={set('timezone')} options={TIMEZONES} />
          </Field>
        </div>
        <FormError message={fe.message} />
        <Button type="submit" size="lg" className="w-full" loading={signup.isPending}>
          Create my firm
        </Button>
        <p className="text-center text-xs leading-relaxed text-muted-foreground">{workspace.AI_PRIVACY_NOTE}</p>
      </form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

function safeRedirect(value: string | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

export function LoginPage() {
  const qc = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const redirect = safeRedirect(useSearchParam('redirect'));
  const [form, setForm] = useState({ email: '', password: '' });
  const [unverified, setUnverified] = useState<string | null>(null);
  const fe = useFormErrors();

  const login = useMutation({
    mutationFn: () => api.post<AuthResponse>('/api/auth/login', { email: form.email.trim(), password: form.password }),
    onSuccess: async (res) => {
      qc.setQueryData(ME_KEY, res.me);
      if (redirect) router.history.push(redirect);
      else await navigate({ to: '/dashboard' });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'email_unverified') setUnverified(form.email.trim());
      else fe.fromError(err);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    fe.reset();
    const errs: Record<string, string> = {};
    if (!form.email.trim()) errs.email = 'Enter your email.';
    if (!form.password) errs.password = 'Enter your password.';
    if (Object.keys(errs).length > 0) {
      fe.setLocal(errs);
      return;
    }
    login.mutate();
  };

  if (unverified) return <CheckEmail email={unverified} />;

  return (
    <AuthLayout
      logo={<LogoLink />}
      title="Sign in to LawBee"
      description="Welcome back."
      footer={
        <>
          New to LawBee?{' '}
          <Link to="/signup" className="font-medium text-foreground underline-offset-4 hover:underline">
            Start a free trial
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={submit} className="space-y-4">
        <Field label="Email" htmlFor="email" error={fe.errors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            aria-invalid={fe.errors.email ? true : undefined}
          />
        </Field>
        <Field label="Password" htmlFor="password" error={fe.errors.password}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            aria-invalid={fe.errors.password ? true : undefined}
          />
        </Field>
        <div className="-mt-1 text-right">
          <Link to="/forgot-password" className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
            Forgot your password?
          </Link>
        </div>
        <FormError message={fe.message} />
        <Button type="submit" size="lg" className="w-full" loading={login.isPending}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Accept invitation
// ---------------------------------------------------------------------------

type InviteInfo = { email: string; firmName: string; role: string };

export function InvitePage() {
  const token = useParam('token');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const invite = useQuery({
    queryKey: ['public', 'invite', token],
    queryFn: () => api.get<InviteInfo>(`/api/auth/invite/${encodeURIComponent(token)}`),
    retry: false,
  });
  const [form, setForm] = useState({ name: '', password: '' });
  const fe = useFormErrors();

  const accept = useMutation({
    mutationFn: () => api.post<AuthResponse>('/api/auth/accept-invite', { token, name: form.name.trim(), password: form.password }),
    onSuccess: async (res) => {
      qc.setQueryData(ME_KEY, res.me);
      await navigate({ to: '/dashboard' });
    },
    onError: fe.fromError,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    fe.reset();
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Enter your name.';
    if (form.password.length < MIN_PASSWORD) errs.password = `Use at least ${MIN_PASSWORD} characters.`;
    if (Object.keys(errs).length > 0) {
      fe.setLocal(errs);
      return;
    }
    accept.mutate();
  };

  if (invite.isLoading) return <LoadingState label="Checking your invitation" className="min-h-dvh" />;

  if (invite.error || !invite.data) {
    return (
      <AuthLayout logo={<LogoLink />} title="Invitation unavailable">
        {isNotFound(invite.error) || !invite.data ? (
          <InvalidLink
            title="This invitation is not valid or has expired"
            description="Ask the person who invited you to send a new invitation from LawBee settings."
          />
        ) : (
          <div className="space-y-4 text-center">
            <FormError message={errorMessage(invite.error)} />
            <Button variant="outline" onClick={() => void invite.refetch()}>
              Try again
            </Button>
          </div>
        )}
      </AuthLayout>
    );
  }

  const roleLabel = (workspace.ROLE_LABELS as Record<string, string>)[invite.data.role] ?? invite.data.role;

  return (
    <AuthLayout
      logo={<LogoLink />}
      title={`Join ${invite.data.firmName}`}
      description={
        <>
          You were invited as <span className="font-medium text-foreground">{roleLabel}</span>.
        </>
      }
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={submit} className="space-y-4">
        <Field label="Email" htmlFor="invite-email">
          <Input id="invite-email" value={invite.data.email} readOnly disabled />
        </Field>
        <Field label="Your name" htmlFor="name" error={fe.errors.name} required>
          <Input
            id="name"
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            aria-invalid={fe.errors.name ? true : undefined}
          />
        </Field>
        <Field label="Choose a password" htmlFor="password" error={fe.errors.password} description={fe.errors.password ? undefined : `At least ${MIN_PASSWORD} characters.`} required>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            aria-invalid={fe.errors.password ? true : undefined}
          />
        </Field>
        <FormError message={fe.message} />
        <Button type="submit" size="lg" className="w-full" loading={accept.isPending}>
          Accept invitation
        </Button>
      </form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Email verification and password reset
// ---------------------------------------------------------------------------

export function VerifyEmailPage() {
  const token = useSearchParam('token') ?? '';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const verify = useMutation({
    mutationFn: () => api.post<{ ok: boolean; me: Me | null }>('/api/auth/verify-email', { token }),
    onSuccess: async (res) => {
      if (res.me) {
        qc.setQueryData(ME_KEY, res.me);
        await navigate({ to: '/onboarding' });
      } else await navigate({ to: '/login' });
    },
  });
  // Tokens are single use: fire once even when StrictMode runs effects twice.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    verify.mutate();
  }, [token, verify]);
  if (!token || verify.isError) {
    return (
      <AuthLayout logo={<LogoLink />} title="Link not valid">
        <InvalidLink title="This confirmation link is invalid, expired or already used" description="Sign in with your email and password to get a new link." />
        <Link to="/login" className="mt-4 block text-center text-sm font-medium text-foreground underline-offset-4 hover:underline">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }
  return <LoadingState label="Confirming your email" className="min-h-dvh" />;
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useMutation({
    mutationFn: () => api.post('/api/auth/forgot-password', { email: email.trim() }),
    onSuccess: () => setSent(true),
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    request.mutate();
  };
  return (
    <AuthLayout
      logo={<LogoLink />}
      title="Reset your password"
      description={sent ? undefined : 'Enter your email and we will send you a link to choose a new password.'}
      footer={
        <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <p className="text-center text-sm text-muted-foreground">If an account exists for {email.trim()}, a reset link is on its way. It works once and expires in 1 hour.</p>
      ) : (
        <form noValidate onSubmit={submit} className="space-y-4">
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <FormError message={error} />
          <Button type="submit" size="lg" className="w-full" loading={request.isPending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const token = useSearchParam('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const reset = useMutation({
    mutationFn: () => api.post('/api/auth/reset-password', { token, password }),
    onSuccess: async () => {
      await navigate({ to: '/login' });
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    reset.mutate();
  };
  if (!token) {
    return (
      <AuthLayout logo={<LogoLink />} title="Link not valid">
        <InvalidLink title="This reset link is incomplete" description="Ask for a new link from the sign-in page." />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout logo={<LogoLink />} title="Choose a new password" description="You will be signed out everywhere else.">
      <form noValidate onSubmit={submit} className="space-y-4">
        <Field label="New password" htmlFor="password" description={`At least ${MIN_PASSWORD} characters.`}>
          <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <FormError message={error} />
        <Button type="submit" size="lg" className="w-full" loading={reset.isPending}>
          Save password
        </Button>
        <Link to="/forgot-password" className="block text-center text-sm text-muted-foreground underline-offset-4 hover:underline">
          Need a new link?
        </Link>
      </form>
    </AuthLayout>
  );
}
